import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import {
  MUTED,
  ERROR_COLOR,
  Note,
  TextArea,
  CodeArea,
  Tokens,
  Token,
  Problem,
  MessagePreview,
  FieldError,
  PlaceholderList,
  Placeholder,
  ModeSwitch,
  Column,
  tokenInserter,
} from './editorParts'
import {
  SITUATIONS,
  compile,
  parse,
  previewIn,
  sentenceProblem,
} from './linkMessageTemplate'

// Shown next to the code editor, the only place a clinic still types Liquid.
// The yes/no ones only mean something inside an {% if %}.
const LINK_PLACEHOLDERS = [
  '{{patient.first_name}}',
  '{{patient.last_name}}',
  '{{patient.full_name}}',
  '{{clinic.name}}',
  '{{reschedule.url}}',
  '{{reschedule.requested_day}}',
  '{{reschedule.requested_day_available}}',
  '{{reschedule.out_of_horizon}}',
  '{{reschedule.closed}}',
  '{{reschedule.same_day}}',
  '{{reschedule.reason}}',
  '{{reschedule.nearest_day}}',
  '{{reschedule.suggested_day}}',
  '{{reschedule.suggested_times}}',
  // A list, not a phrase: loop over it and print each `slot.time`.
  '{% for slot in reschedule.suggested_slots %}',
  '{{slot.time}}',
]

const GROUPS = [
  { key: 'times', label: 'When we found times to offer' },
  { key: 'closing', label: 'Last line, under the times' },
  { key: 'noTimes', label: 'When there are no times to name' },
]

const Picker = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin-bottom: 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
`

const Select = styled.select`
  max-width: 28rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background-color: #fff;
  font-size: 0.875rem;
  color: #111827;
`

const Hint = styled.p`
  margin: 0 0 0.5rem;
  font-size: 0.75rem;
  color: ${MUTED};
`

const Attention = styled.div`
  margin-top: 1rem;
  font-size: 0.75rem;
  color: ${ERROR_COLOR};

  button {
    padding: 0;
    border: none;
    background: none;
    color: inherit;
    font-size: inherit;
    text-decoration: underline;
    cursor: pointer;
  }
`

// What the patient would read in this situation. A message with times gets
// the closing line under it, the same way the template puts it there; a
// closing line is shown under the most common message, so it reads in place.
const fullMessage = (situation, sentences) => {
  if (situation.group === 'closing') {
    return `${previewIn(
      'timesAvailable',
      sentences.timesAvailable
    )}\n${previewIn(situation.key, sentences[situation.key])}`
  }

  const sentence = previewIn(situation.key, sentences[situation.key])
  if (situation.group !== 'times') return sentence

  const closing = previewIn('closingMany', sentences.closingMany)
  return closing ? `${sentence}\n${closing}` : sentence
}

// One message at a time: the clinic picks the situation, edits one box and
// reads it as the patient would. The rest wait in the picker, marked when they
// were edited or need attention.
const LinkMessageEditor = ({ value, onChange, error }) => {
  const [sentences, setSentences] = useState(() => parse(value))
  const [saved] = useState(() => parse(value))
  const [mode, setMode] = useState(() => (parse(value) ? 'simple' : 'code'))
  const [current, setCurrent] = useState(SITUATIONS[0].key)
  const [cannotSimplify, setCannotSimplify] = useState(false)
  const box = useRef(null)

  const setSentence = (key, text) => {
    const next = { ...sentences, [key]: text }
    setSentences(next)
    onChange(compile(next))
  }

  const insertToken = tokenInserter(box, sentences?.[current] ?? '', text =>
    setSentence(current, text)
  )

  const showSimple = () => {
    const parsed = parse(value)
    if (!parsed) {
      setCannotSimplify(true)
      return
    }
    setSentences(parsed)
    setCannotSimplify(false)
    setMode('simple')
  }

  if (mode === 'code') {
    return (
      <Column>
        <Note>
          The whole message as a Liquid template. Only the placeholders below
          exist; anything else is refused when you save. The yes/no ones
          (requested_day_available, out_of_horizon, closed, same_day) are read
          inside an {'{% if %}'}, never printed.
        </Note>
        <CodeArea
          aria-label="Message template"
          rows={18}
          spellCheck={false}
          $invalid={Boolean(error)}
          value={value}
          onChange={event => onChange(event.target.value)}
        />
        <PlaceholderList>
          {LINK_PLACEHOLDERS.map(placeholder => (
            <Placeholder key={placeholder}>{placeholder}</Placeholder>
          ))}
        </PlaceholderList>
        {error && <FieldError>{error}</FieldError>}
        <ModeSwitch type="button" onClick={showSimple}>
          Edit one situation at a time
        </ModeSwitch>
        {cannotSimplify && (
          <Problem $blocking>
            This message has logic of its own, so it can only be edited as code.
          </Problem>
        )}
      </Column>
    )
  }

  const situation = SITUATIONS.find(({ key }) => key === current)
  const text = sentences[current]
  const problem = sentenceProblem(current, text)
  const message = fullMessage(situation, sentences)
  const blocked = SITUATIONS.filter(
    ({ key }) =>
      key !== current && sentenceProblem(key, sentences[key])?.blocking
  )

  const optionLabel = ({ key, title }) => {
    if (sentenceProblem(key, sentences[key])?.blocking)
      return `${title} (needs fixing)`
    if (saved && sentences[key] !== saved[key]) return `${title} (edited)`
    return title
  }

  return (
    <Column>
      <Note>
        The text the patient gets with their link. It changes a little with what
        they asked for, so pick a situation to see and edit its version.
      </Note>

      <Picker>
        Situation
        <Select
          value={current}
          onChange={event => setCurrent(event.target.value)}
        >
          {GROUPS.map(group => (
            <optgroup key={group.key} label={group.label}>
              {SITUATIONS.filter(({ group: g }) => g === group.key).map(
                option => (
                  <option key={option.key} value={option.key}>
                    {optionLabel(option)}
                  </option>
                )
              )}
            </optgroup>
          ))}
        </Select>
      </Picker>

      {situation.hint && <Hint>{situation.hint}</Hint>}

      <TextArea
        aria-label="Message"
        ref={box}
        rows={situation.group === 'closing' ? 2 : 4}
        $invalid={problem?.blocking}
        value={text}
        onChange={event => setSentence(current, event.target.value)}
      />
      <Tokens>
        Add:
        {situation.tokens.map(label => (
          <Token key={label} type="button" onClick={() => insertToken(label)}>
            {label}
          </Token>
        ))}
      </Tokens>
      {problem && (
        <Problem $blocking={problem.blocking}>{problem.message}</Problem>
      )}

      <MessagePreview message={message} />

      {blocked.length > 0 && (
        <Attention>
          {'Also needs fixing: '}
          {blocked.map(({ key, title }, index) => (
            <React.Fragment key={key}>
              {index > 0 && ', '}
              <button type="button" onClick={() => setCurrent(key)}>
                {title}
              </button>
            </React.Fragment>
          ))}
        </Attention>
      )}

      {error && <FieldError>{error}</FieldError>}

      <ModeSwitch type="button" onClick={() => setMode('code')}>
        Edit as code
      </ModeSwitch>
    </Column>
  )
}

export default LinkMessageEditor
